import React, { useContext, useState } from 'react'
import { observer } from 'mobx-react-lite'
import { Pencil, Trash3 } from 'react-bootstrap-icons'
import { TimelineContext } from 'contexts/TimelineContext'
import { CampaignContext } from 'contexts/CampaignContext'
import axiosService from '../../../../../../utils/AxiosService'
import { useParams } from 'react-router-dom'
import { toastError } from '../../../../../../components/FormComponents/Toasts'
import CampaignScenariosProjectAsset from '../../../../../../models/CampaignScenariosProjectAsset'
import ProjectTimelineAssetItem from '../../../../../Shared/TimelineParts/ProjectTimelineAssetItem'
import ScenarioTimelineAssetItem from '../../../../../Shared/TimelineParts/ScenarioTimelineAssetItem'
import EditAssetModal from '../TimelineSidebarParts/EditAssetModal'
import { usePmAssetListItem } from '../TimelineSidebarParts/usePmAssetListItem'
import { useReadOnly } from '../../../../../../contexts/ReadOnlyContext'

const ScenarioTimelineActionItem = observer(function ScenarioTimelineActionItem({
  timelineAsset,
  isTemporary,
  onDelete,
}) {
  const {
    readOnly,
    note,
    editModalOpen,
    campaignStartDate,
    campaignEndDate,
    currentStartDate,
    currentEndDate,
    editModalProps,
    setEditModalOpen,
    handleEditSave,
  } = usePmAssetListItem({ asset: timelineAsset, isUsedInTimeline: true })

  const menuItems = readOnly ? [] : [
    {
      key: 'edit',
      label: 'Edit',
      icon: <Pencil className="text-rb-green" />,
      onClick: () => setEditModalOpen(true),
    },
    {
      key: 'remove',
      label: 'Remove',
      icon: <Trash3 className="text-red-500" />,
      onClick: onDelete,
    },
  ]

  return (
    <>
      <ScenarioTimelineAssetItem
        timelineAsset={timelineAsset}
        isTemporary={isTemporary}
        menuItems={menuItems}
      />
      <div onClick={(e) => e.stopPropagation()}>
        <EditAssetModal
          isOpen={editModalOpen}
          onClose={() => setEditModalOpen(false)}
          asset={timelineAsset}
          initialStartDate={currentStartDate}
          initialEndDate={currentEndDate}
          initialNote={note}
          initialName={timelineAsset.name}
          campaignStartDate={campaignStartDate}
          campaignEndDate={campaignEndDate}
          onSave={handleEditSave}
          {...editModalProps}
        />
      </div>
    </>
  )
})

export default observer(function TimelineAssetItem({ timelineAsset }) {
  const { timelineState, toggleChangeDatesModal } = useContext(TimelineContext)
  const { campaignScenarios, primaryScenarioId } = useContext(CampaignContext)
  const { id } = useParams()
  const readOnly = useReadOnly()

  const isTemporary = String(timelineAsset.id).startsWith('temp-')

  const isProjectAsset = timelineAsset instanceof CampaignScenariosProjectAsset

  const handleDelete = (e) => {
    e?.stopPropagation()

    const newItems = new Map(timelineState.timelineItems)
    const currentItems = newItems.get(timelineAsset.start_date) || []
    newItems.set(timelineAsset.start_date, currentItems.filter(item => String(item.id) !== String(timelineAsset.id)))
    timelineState.timelineItems = newItems

    const campaignScenario = campaignScenarios[0]

    isProjectAsset ?
      campaignScenario.campaign_scenarios_project_assets = campaignScenario.campaign_scenarios_project_assets.filter(a => +a.id !== +timelineAsset.id)
    :
      campaignScenario.actions = campaignScenario.actions.map(a =>
        a.id === timelineAsset.id ? {...a, start_date: null, end_date: null} : a
      )

    const apiRequest = isProjectAsset ?
      axiosService.delete(`/api/project_manager/campaigns/${id}/campaign_scenarios/${primaryScenarioId}/campaign_scenarios_project_assets/${timelineAsset.id}`)
    :
      axiosService.patch(
        `/api/project_manager/campaigns/${id}/campaign_scenarios/${primaryScenarioId}/actions/${timelineAsset.id}`,
        { campaign_scenario_action: { start_date: null, end_date: null } }
      )

    apiRequest.then(() => {
      // If successful, the item is already removed from the UI
    }).catch(error => {
      console.error('Error removing timeline item:', error)
      // Revert the deletion if there's an error
      const revertItems = new Map(timelineState.timelineItems)
      const currentItems = revertItems.get(timelineAsset.start_date) || []
      revertItems.set(timelineAsset.start_date, [...currentItems, timelineAsset])
      timelineState.timelineItems = revertItems
      toastError(`Failed to remove the ${isProjectAsset ? 'project asset' : 'action'}. Please try again.`)
    })
  }

  const projectAssetMenuItems = readOnly ? [] : [
    {
      key: 'edit',
      label: 'Edit',
      icon: <Pencil className="text-rb-green" />,
      onClick: () => toggleChangeDatesModal(timelineAsset),
    },
    {
      key: 'remove',
      label: 'Remove',
      icon: <Trash3 className="text-red-500" />,
      onClick: handleDelete,
    },
  ]

  if (!isProjectAsset) {
    return (
      <ScenarioTimelineActionItem
        timelineAsset={timelineAsset}
        isTemporary={isTemporary}
        onDelete={handleDelete}
      />
    )
  }

  return (
    <ProjectTimelineAssetItem
      timelineAsset={timelineAsset}
      isTemporary={isTemporary}
      menuItems={projectAssetMenuItems}
    />
  )
})